<template>
  <div class="ui form">
    <div class="field">
      <div class="global-h4">Destination Contact Name</div>
      <p class="cms">Who is the primary contact for this visit? (if the creator has questions or businesses ask who they are working with)</p>
      <div
        :class="{
          'form-error-highlight': true,
          'form-error-highlight--bg-visible': errors.includes('Contact Name'),
        }"
        style="margin-bottom: 25px"
      >
        <input class="global-text-input" type="text" v-model="collabInput.destinationContactName" ref="destinationContactNameInput" style="width: 320px; max-width: 320px" @blur="emit('revalidate')" />
      </div>
    </div>

    <div class="field">
      <div class="global-h4">Contact Phone Number</div>
      <p class="cms">What is a good mobile number for the creator to reach in case of last-minute coordination needs? We'll text a code to verify it.</p>
      <div
        :class="{
          'form-error-highlight': true,
          'form-error-highlight--bg-visible': isPhoneErrorHighlighted,
        }"
      >
        <DestinationPhoneVerification
          :customerId="communityId"
          :collabInputId="collabInput.id"
          :phoneNumber="collabInput.destinationPhoneNumber"
          :isVerified="collabInput.destinationPhoneNumberIsVerified"
          @verified="onPhoneVerified"
          @update:phoneNumber="onPhoneEnteredWithoutVerifying"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint vue/no-mutating-props: 0 */
import { computed, ref, onMounted, nextTick } from "vue";
import { ContactPhoneNotVerifiedError } from "@logic/CollabInputValidator";

// Types
import { CollabInput } from "@contracts/collab";

// Components
import DestinationPhoneVerification from "../DestinationPhoneVerification.vue";

const props = defineProps({
  communityId: { type: String, required: true },
  collabInput: { type: Object as () => CollabInput | null, required: true },
  errors: { type: Array<string>, required: true },
});

const emit = defineEmits<{
  // Asks the wizard to re-check this step so errors it's already showing go away as they get fixed
  (e: "revalidate"): void;
}>();

const destinationContactNameInput = ref<HTMLInputElement | null>(null);

onMounted(() => {
  nextTick(() => destinationContactNameInput.value?.focus());
});

// A verified number is never an error, even if the wizard's error list hasn't been refreshed yet
const isPhoneErrorHighlighted = computed<boolean>(
  () => !props.collabInput.destinationPhoneNumberIsVerified && (props.errors.includes("Contact Phone Number") || props.errors.includes(ContactPhoneNotVerifiedError))
);

function onPhoneVerified(updatedCollabInput: CollabInput) {
  // The server saved the number while confirming the code, so take its _etag too or the wizard's next save looks like a conflict
  props.collabInput.destinationPhoneNumber = updatedCollabInput.destinationPhoneNumber;
  props.collabInput.destinationPhoneNumberIsVerified = updatedCollabInput.destinationPhoneNumberIsVerified;
  props.collabInput._etag = updatedCollabInput._etag;
  emit("revalidate");
}

function onPhoneEnteredWithoutVerifying(phoneNumber: string) {
  props.collabInput.destinationPhoneNumber = phoneNumber;
  props.collabInput.destinationPhoneNumberIsVerified = false;
  emit("revalidate");
}
</script>

<style scoped lang="scss">
@import "./form-error-highlight.scss";
</style>
